import {type CellAddress, type ItemId, useBoardCard} from '@github-ui/board-core'
import {useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useCallback} from 'react'

import styles from './BoardCardFrame.module.css'
import {BoardCardMenu} from './BoardCardMenu'
import {BoardCardMenuButtonContext, type BoardCardMenuPlacement} from './BoardCardMenuButton'

/**
 * Where a card sits in its cell's cards, for a cell that draws only some of them.
 */
export type BoardCardPlace = {
  /** Zero-based index among the cards the cell has loaded. */
  index: number
  /**
   * Registers this card with its cell's window, which measures it rather than assuming a
   * height. The window reads {@link BoardCardPlace.index} back off the element to know
   * which card it measured, which is why the two arrive together.
   */
  measureRef?: (node: Element | null) => (() => void) | undefined
}

/** The menu a card draws, in its top corner unless `placement` is `'inline'`. */
export type BoardCardFrameMenu = {
  /**
   * Names the button that opens the menu. `BoardCellFrame` builds it from
   * `labels.cardActions` and the item's own label.
   */
  label: string
  /**
   * The caller's own menu content, such as `ActionList.Item`s, shown above the move
   * controls.
   */
  actions?: ReactNode
  /** The caller's own menu content shown after the move controls, each bringing its own divider. */
  trailingActions?: ReactNode
  /** Called when the pointer enters or focus reaches the menu button, before the menu opens. */
  onIntent?: () => void
  /**
   * Where the menu button sits. With `'inline'`, the card content renders `BoardCardMenuButton`,
   * and a card that does not render it has no menu button.
   * @default 'corner'
   */
  placement?: BoardCardMenuPlacement
}

export type BoardCardFrameProps = {
  itemId: ItemId
  /** The cell this card is in. The same item may sit in two cells on a multi-value board. */
  address: CellAddress
  /**
   * Names the card's `<section>`. `BoardCellFrame` passes the item's own label, from
   * `getItemLabel`, so a reader navigating by region hears the card's title.
   *
   * Optional, because a caller assembling its own cell may name the section another way,
   * and a `<section>` with no name is a plain container rather than a region.
   */
  label?: string
  /**
   * Where this card sits among the cell's cards, so the cell's window can measure it and
   * know which card the measurement came from.
   *
   * Optional, because a caller assembling its own cell out of these frames may window
   * nothing, in which case it draws every card and needs no place.
   */
  place?: BoardCardPlace
  /**
   * Marks the edge of this card a held card would land against, as `data-drop-insertion`.
   *
   * Set on one card of a cell at a time, by whatever is drawing the cell, from
   * `useBoardCell`'s `dropIndex`. `after` is only ever the last card the cell has drawn;
   * every other position is `before` the card it names. The frame draws nothing for it:
   * `BoardCellFrame` opens a room at that place instead.
   *
   * Nothing is announced for it. It answers a pointer gesture, and the reader it is
   * addressed to is the one watching a card follow their pointer.
   */
  insertion?: 'before' | 'after'
  /**
   * Draws a menu button, whose menu holds the moves the board offers for this card and then
   * `actions`. Absent draws no menu.
   *
   * `BoardCellFrame` passes one for every card on a board that moves cards, and for every
   * card the caller gave actions.
   */
  menu?: BoardCardFrameMenu
  children?: ReactNode
  className?: string
}

/**
 * A card's shell: border, radius, padding, and the menu button.
 *
 * It is a `<section>` named by `label`. A card holds a title and a handful of fields, which
 * is a self-contained region a reader navigates to and reads as a unit. The cards in a cell
 * are not a list: a cell windows its cards, so a list's item count would only ever describe
 * the ones drawn, and the cell's own name already states how many it holds.
 *
 * It is a component rather than something the cell renders inline because it calls its
 * own hook, and a hook cannot be called inside a callback that runs once per card. That
 * is how the roving tabindex, the selection gesture and the drag source all arrived
 * without any caller changing.
 */
export function BoardCardFrame({
  itemId,
  address,
  label,
  place,
  insertion,
  menu,
  children,
  className,
}: BoardCardFrameProps) {
  const {cardProps, isSelected, isDragging, isDraggable, hasLanded, reportDialogOpen} = useBoardCard(itemId, address)
  const ref = useMergedRefs(cardProps.ref, place?.measureRef)
  const isTabStop = cardProps.tabIndex === 0
  const cornerMenu = menu?.placement === 'inline' ? undefined : menu
  const inlineMenu = menu?.placement === 'inline' ? menu : undefined
  const {label: menuLabel, actions, trailingActions, onIntent} = inlineMenu ?? {}
  const renderInlineMenuButton = useCallback(
    ({className: buttonClassName, revealOnHover}: {className: string | undefined; revealOnHover: boolean}) =>
      menuLabel === undefined ? null : (
        <BoardCardMenu
          itemId={itemId}
          address={address}
          label={menuLabel}
          actions={actions}
          trailingActions={trailingActions}
          onIntent={onIntent}
          isTabStop={isTabStop}
          reportDialogOpen={reportDialogOpen}
          className={clsx(styles.CardMenuInline, buttonClassName)}
          revealOnHover={revealOnHover}
        />
      ),
    [itemId, address, menuLabel, actions, trailingActions, onIntent, isTabStop, reportDialogOpen],
  )

  return (
    <section
      {...cardProps}
      ref={ref}
      aria-label={label}
      // Read back by the cell's window to know which card a measurement came from. It is
      // here rather than in `cardProps` because it describes where this element sits in a
      // drawn list, which is the styled layer's business rather than the headless one's.
      data-index={place?.index}
      // A styling hook and nothing else. This package draws a selected card no differently
      // from any other, because selection has no treatment yet and inventing one is not
      // this release's to make; a caller that acts on a selection styles this in the
      // meantime. Absent rather than `false`, so `[data-selected]` matches the selected
      // cards and only those.
      data-selected={isSelected ? '' : undefined}
      // The card a pointer has hold of, which this package rings and leaves exactly where
      // it is: `BoardDraggedCard` draws the copy that follows the pointer. On from the
      // press, so the ring answers the grab rather than the movement after it. Absent
      // rather than `false`, on the same rule `data-selected` follows above.
      data-dragging={isDragging ? '' : undefined}
      // The card that cannot be picked up, which stops it offering a `cursor: grab` it
      // cannot honour and gives a caller somewhere to say more. Absent rather than `false`,
      // on the same rule the two above follow.
      //
      // `isDraggable` rather than `dragRefusal`, because the cursor answers whether the
      // gesture exists and the refusal answers why the caller withheld it, and those come
      // apart: a card with no id and every card on a board with no `onItemsMove` cannot be
      // picked up and carries no reason for it. Keyed on the reason, each of those would
      // offer a grab that goes nowhere.
      //
      // One attribute rather than a `data-draggable` beside it: two attributes describing
      // one boolean is two things to keep in step. The negative is the one that carries a
      // fact, and `dragRefusal` is still what a caller reads to say what the reason was,
      // where there is one.
      data-drag-disabled={isDraggable ? undefined : ''}
      // Which edge of this card a drop would land against, or absent because no drop is
      // being aimed here. The value carries the edge rather than a bare presence, because
      // the end of a cell is a position too and it is the only one below a card rather than
      // above one.
      data-drop-insertion={insertion}
      // The card a drop just moved here, which pulses once. Absent rather than `false`, on
      // the same rule the attributes above follow.
      data-landed={hasLanded ? '' : undefined}
      // Room at the inline end for the corner menu button, so the card's content does not run
      // under it. An inline button takes its room inside the content.
      data-card-menu={cornerMenu === undefined ? undefined : ''}
      className={clsx(styles.CardFrame, className)}
    >
      {/* Always present, so a card that gains or loses its menu keeps its content mounted. */}
      <BoardCardMenuButtonContext value={inlineMenu === undefined ? null : renderInlineMenuButton}>
        {children}
      </BoardCardMenuButtonContext>
      {/*
        After the content, so a reader hears the card before the button that acts on it,
        and drawn in the top corner by the stylesheet.
      */}
      {cornerMenu === undefined ? null : (
        <BoardCardMenu
          itemId={itemId}
          address={address}
          label={cornerMenu.label}
          actions={cornerMenu.actions}
          trailingActions={cornerMenu.trailingActions}
          onIntent={cornerMenu.onIntent}
          isTabStop={isTabStop}
          reportDialogOpen={reportDialogOpen}
          className={styles.CardMenu}
        />
      )}
    </section>
  )
}
